import { OfferRequestEstimatedVolume } from '@houzy/schemas/offer-request-management';
import {
    reimbursementReasonRequiresProof,
    type PartnerCockpitContactAttemptCount,
    type PartnerCockpitReimbursementReason,
} from '@houzy/schemas/partner-cockpit/reimbursement';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useReimbursementDraft } from '../state/reimbursementDraftContext';
import { ReimbursementProofUpload } from './ReimbursementProofUpload';

const VOLUMES = [
    OfferRequestEstimatedVolume.Below5000,
    OfferRequestEstimatedVolume.From5000Below10000,
    OfferRequestEstimatedVolume.From10000Below20000,
    OfferRequestEstimatedVolume.From20000Below50000,
    OfferRequestEstimatedVolume.From50000,
] as const;

const VOLUME_LABEL_KEYS: Record<(typeof VOLUMES)[number], string> = {
    Below5000: 'OFFER_REQUESTS.FILTERS.SIZE.UNDER_5K',
    From5000Below10000: 'OFFER_REQUESTS.FILTERS.SIZE.FROM_5K_TO_10K',
    From10000Below20000: 'OFFER_REQUESTS.FILTERS.SIZE.FROM_10K_TO_20K',
    From20000Below50000: 'OFFER_REQUESTS.FILTERS.SIZE.FROM_20K_TO_50K',
    From50000: 'OFFER_REQUESTS.FILTERS.SIZE.OVER_50K',
};

const CONTACT_COUNTS = ['Once', 'TwoThreeTimes', 'MoreThenThreeTimes'] as const;
const CONTACT_COUNT_LABEL_KEYS: Record<PartnerCockpitContactAttemptCount, string> = {
    Once: 'REIMBURSEMENT.DETAILS.CONTACT_COUNT.ONCE',
    TwoThreeTimes: 'REIMBURSEMENT.DETAILS.CONTACT_COUNT.TWO_THREE',
    MoreThenThreeTimes: 'REIMBURSEMENT.DETAILS.CONTACT_COUNT.MORE_THAN_THREE',
};

const REASONS_WITH_CONTACT_CHANNELS: ReadonlySet<PartnerCockpitReimbursementReason> = new Set([
    'UserNotReachable',
    'UserNotInterested',
]);

function ContactChannelFields(): ReactElement {
    const { t } = useTranslation();
    const { draft, updateDraft } = useReimbursementDraft();

    return (
        <FormControl component="fieldset">
            <FormLabel component="legend">{t('REIMBURSEMENT.DETAILS.CONTACT_CHANNELS')}</FormLabel>
            <FormGroup>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={draft.contactedByPhone}
                            onChange={(event) => updateDraft({ contactedByPhone: event.target.checked })}
                        />
                    }
                    label={t('REIMBURSEMENT.DETAILS.CONTACT_BY_PHONE')}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={draft.contactedByEmail}
                            onChange={(event) => updateDraft({ contactedByEmail: event.target.checked })}
                        />
                    }
                    label={t('REIMBURSEMENT.DETAILS.CONTACT_BY_EMAIL')}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={draft.contactedBySmsOrWhatsApp}
                            onChange={(event) => updateDraft({ contactedBySmsOrWhatsApp: event.target.checked })}
                        />
                    }
                    label={t('REIMBURSEMENT.DETAILS.CONTACT_BY_SMS')}
                />
            </FormGroup>
        </FormControl>
    );
}

export function ReimbursementReasonFields({ reason }: { reason: PartnerCockpitReimbursementReason }): ReactElement {
    const { t } = useTranslation();
    const { draft, updateDraft } = useReimbursementDraft();

    return (
        <Stack spacing={3}>
            {reason === 'TooManyCreditsBilled' && (
                <TextField
                    select
                    required
                    label={t('REIMBURSEMENT.DETAILS.ESTIMATED_VOLUME')}
                    value={draft.estimatedVolume ?? ''}
                    onChange={(event) =>
                        updateDraft({ estimatedVolume: event.target.value as (typeof VOLUMES)[number] })
                    }>
                    {VOLUMES.map((volume) => (
                        <MenuItem key={volume} value={volume}>
                            {t(VOLUME_LABEL_KEYS[volume])}
                        </MenuItem>
                    ))}
                </TextField>
            )}

            {reason === 'NotEnoughCapacityOrOnHoliday' && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                        required
                        type="date"
                        label={t('REIMBURSEMENT.DETAILS.ABSENCE_FROM')}
                        value={draft.absenceFrom}
                        onChange={(event) => updateDraft({ absenceFrom: event.target.value })}
                        slotProps={{ inputLabel: { shrink: true } }}
                        fullWidth
                    />
                    <TextField
                        required
                        type="date"
                        label={t('REIMBURSEMENT.DETAILS.ABSENCE_TO')}
                        value={draft.absenceTo}
                        onChange={(event) => updateDraft({ absenceTo: event.target.value })}
                        slotProps={{ inputLabel: { shrink: true } }}
                        error={draft.absenceTo !== '' && draft.absenceFrom > draft.absenceTo}
                        helperText={
                            draft.absenceTo !== '' && draft.absenceFrom > draft.absenceTo
                                ? t('REIMBURSEMENT.DETAILS.ABSENCE_ORDER_ERROR')
                                : undefined
                        }
                        fullWidth
                    />
                </Stack>
            )}

            {REASONS_WITH_CONTACT_CHANNELS.has(reason) && <ContactChannelFields />}

            {reason === 'UserNotReachable' && (
                <TextField
                    select
                    required
                    label={t('REIMBURSEMENT.DETAILS.CONTACT_COUNT_LABEL')}
                    value={draft.numberOfTimesUserWasContacted ?? ''}
                    onChange={(event) =>
                        updateDraft({
                            numberOfTimesUserWasContacted: event.target.value as PartnerCockpitContactAttemptCount,
                        })
                    }>
                    {CONTACT_COUNTS.map((count) => (
                        <MenuItem key={count} value={count}>
                            {t(CONTACT_COUNT_LABEL_KEYS[count])}
                        </MenuItem>
                    ))}
                </TextField>
            )}

            {reimbursementReasonRequiresProof(reason) && <ReimbursementProofUpload />}

            <TextField
                required
                multiline
                minRows={4}
                label={t('REIMBURSEMENT.DETAILS.FEEDBACK_LABEL')}
                placeholder={t('REIMBURSEMENT.DETAILS.FEEDBACK_PLACEHOLDER')}
                value={draft.partnerFeedback}
                onChange={(event) => updateDraft({ partnerFeedback: event.target.value })}
            />
        </Stack>
    );
}
